'use client';

import { useEffect, useState } from 'react';

export type CountdownVariant =
  | 'grid'       // default — 4-col bordered cells
  | 'strip'      // horizontal number+label pairs on #fafafa
  | 'minimal'    // pure typography, no chrome
  | 'dark'       // midnight surface, on-dark palette
  | 'gradient'   // cyber-blue→midnight gradient
  | 'colon'      // colon-separated HH:MM:SS style
  | 'pill'       // each unit as a filled limestone pill
  | 'cards'      // individual floating shadow cards
  | 'impact'     // oversized vw-scaled numbers
  | 'ticker';    // departure-board aesthetic

interface Props {
  airsAt?: string;
  label?: string;
  variant?: CountdownVariant;
}

export function WebinarCountdown({ airsAt, variant = 'grid' }: Props) {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    function tick() { setNow(Date.now()); }
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [airsAt]);

  if (!airsAt || now === null) return null;
  const target = new Date(airsAt).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;

  if (diff <= 0 && diff > -90 * 60_000) {
    return (
      <span className="wbn-countdown wbn-countdown--live">
        <span className="wbn-countdown__live-dot" aria-hidden="true" />
        Live now
      </span>
    );
  }
  if (diff <= -90 * 60_000) return null;

  const totalSeconds = Math.floor(diff / 1000);
  const days    = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours   = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  const cells: Array<{ num: number; unit: string }> = [
    { num: days,    unit: 'Days' },
    { num: hours,   unit: 'Hours' },
    { num: minutes, unit: 'Minutes' },
    { num: seconds, unit: 'Seconds' },
  ];

  // ── Colon variant — number:number style with labels below each ─────────
  if (variant === 'colon') {
    return (
      <div
        className="wbn-cdv wbn-cdv--colon"
        role="timer"
        aria-label="Time until webinar starts"
      >
        {cells.map(({ num, unit }, i) => (
          <span key={unit} className="wbn-cdv__colon-group">
            {/* number + colon on the same row */}
            <span className="wbn-cdv__colon-top">
              <span className="wbn-cdv__colon-num">{pad(num)}</span>
              {i < cells.length - 1 && (
                <span className="wbn-cdv__sep" aria-hidden="true">:</span>
              )}
            </span>
            <span className="wbn-cdv__colon-unit">{unit}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`wbn-cdv wbn-cdv--${variant}`}
      role="timer"
      aria-label="Time until webinar starts"
    >
      {cells.map(({ num, unit }) => (
        <div key={unit} className="wbn-cdv__cell">
          <span className="wbn-cdv__num">{variant === 'grid' ? num : pad(num)}</span>
          <span className="wbn-cdv__unit">{unit}</span>
        </div>
      ))}
    </div>
  );
}
